/* eslint-disable */
// Generated from voia-travel-mobile app/utils/itineraryMapData.ts (types stripped). Keep in sync: edit there, re-run the transpile.
/**
 * Shared itinerary map data logic: same rules as MapboxItineraries and feed posts.
 * Use this to build static map URLs and any other map representation so they stay in sync.
 */
import { projectPins } from './projectPins.js';
function normalizeToDate(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}
function parseDate(v) {
    return v instanceof Date ? v : new Date(v);
}
/**
 * Returns valid pins (with coordinates) sorted by check-in date.
 * Matches MapboxItineraries validPins.
 */
export function getValidPins(pins) {
    return pins
        .filter((p) => typeof p.latitude === 'number' && typeof p.longitude === 'number')
        .sort((a, b) => parseDate(a.checkInDate).getTime() - parseDate(b.checkInDate).getTime());
}
/**
 * Main-route segments between consecutive stays. Transit-typed pins are
 * expected to be filtered out before this is called (see
 * `enrichItineraryMapPins` and `getItineraryMapData`'s `destinationPins`
 * filter, both of which exclude `type === 'transit'`). The canonical
 * model guarantees full pin coverage, so any gap between two adjacent
 * stays is a transit gap and the line should still be drawn.
 */
export function getRouteSegments(destinationPins) {
    if (destinationPins.length < 2)
        return [];
    const sorted = [...destinationPins].sort((a, b) => parseDate(a.checkInDate).getTime() - parseDate(b.checkInDate).getTime());
    const segments = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        const current = sorted[i];
        const next = sorted[i + 1];
        segments.push({
            from: [current.longitude, current.latitude],
            to: [next.longitude, next.latitude],
        });
    }
    return segments;
}
/**
 * Day-visit segments: from each day visit to its associated destination.
 * Same as MapboxItineraries dayVisitLines.
 */
export function getDayVisitSegments(dayVisitPins, destinationPins, dayVisitAssociations) {
    const segments = [];
    dayVisitPins.forEach((dv) => {
        const destId = dayVisitAssociations.get(dv.id);
        if (!destId)
            return;
        const dest = destinationPins.find((d) => d.id === destId);
        if (!dest)
            return;
        segments.push({ from: [dest.longitude, dest.latitude], to: [dv.longitude, dv.latitude] });
    });
    return segments;
}
/**
 * Bbox [minLon, minLat, maxLon, maxLat] with padding. Uses all valid pins (destinations + day visits).
 */
export function getBbox(validPins, paddingRatio = 0.15) {
    if (validPins.length === 0)
        return null;
    const lats = validPins.map((p) => p.latitude);
    const lngs = validPins.map((p) => p.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const rangeLat = maxLat - minLat || 0.02;
    const rangeLng = maxLng - minLng || 0.02;
    return [
        minLng - rangeLng * paddingRatio,
        minLat - rangeLat * paddingRatio,
        maxLng + rangeLng * paddingRatio,
        maxLat + rangeLat * paddingRatio,
    ];
}
/**
 * Build enriched map pins + dayVisit associations from a raw trip doc.
 *
 * Single source of truth for the trip-map data shape: takes tripData,
 * runs it through `projectPins`, and returns pin/dv arrays in the shape
 * MapboxItineraries / getItineraryMapData expect. Canonical day-visits
 * (`trip.dayVisits[]`) and legacy nested day-visits (`pins[]` with
 * type='dayVisit') are both surfaced via `projection.dayVisits`, so
 * callers no longer need date-based matching to associate dvs with
 * destinations — `parentPinId` lives on the projected entry directly.
 *
 *   • Spine pins (type !== 'dayVisit') get checkIn/checkOut from
 *     projection.computedStart / computedEnd.
 *   • Day-visit pins get checkIn = checkOut = projection.computedDay.
 *   • Orphan day-visits (parent removed) are skipped.
 */
export function enrichItineraryMapPins(tripData) {
    const projection = projectPins(tripData);
    const spinePins = projection.pins
        .filter((p) => p.type !== 'dayVisit')
        .map((p) => {
        var _a, _b, _c;
        return ({
            id: p.id,
            name: (_a = p.legacy) === null || _a === void 0 ? void 0 : _a.name,
            latitude: (_b = p.legacy) === null || _b === void 0 ? void 0 : _b.latitude,
            longitude: (_c = p.legacy) === null || _c === void 0 ? void 0 : _c.longitude,
            checkInDate: p.computedStart,
            checkOutDate: p.computedEnd,
            type: p.type,
        });
    });
    const dayVisitPins = projection.dayVisits
        .filter((dv) => !dv.isOrphan)
        .map((dv) => ({
        id: dv.id,
        name: dv.name,
        latitude: dv.latitude,
        longitude: dv.longitude,
        checkInDate: dv.computedDay,
        checkOutDate: dv.computedDay,
        type: 'dayVisit',
        createdAt: dv.createdAt,
    }));
    const dayVisitAssociations = new Map();
    projection.dayVisits.forEach((dv) => {
        if (dv.isOrphan)
            return;
        if (dv.parentPinId)
            dayVisitAssociations.set(dv.id, dv.parentPinId);
    });
    return {
        pins: [...spinePins, ...dayVisitPins],
        dayVisitAssociations,
    };
}
/**
 * Single entry point: same logic as MapboxItineraries / feed posts.
 * Use for static map URL building and any consistent map representation.
 */
export function getItineraryMapData(pins, dayVisitAssociations) {
    const validPins = getValidPins(pins);
    // Stays only — transits and day-visits are excluded from the route-line
    // graph. Transits don't act as waypoints (the line jumps stay→stay);
    // day-visits get their own parent→dv lines via getDayVisitSegments.
    const destinationPins = validPins.filter((p) => p.type !== 'dayVisit' && p.type !== 'transit');
    const dayVisitPins = validPins.filter((p) => p.type === 'dayVisit');
    const assocMap = dayVisitAssociations instanceof Map
        ? dayVisitAssociations
        : new Map(dayVisitAssociations ? Object.entries(dayVisitAssociations) : []);
    const routeSegments = getRouteSegments(destinationPins);
    const dayVisitSegments = getDayVisitSegments(dayVisitPins, destinationPins, assocMap);
    const bbox = getBbox(validPins);
    const pinCoordsForMap = validPins.map((p) => [p.longitude, p.latitude]);
    return {
        validPins,
        destinationPins,
        dayVisitPins,
        routeSegments,
        dayVisitSegments,
        bbox,
        pinCoordsForMap,
    };
}
